"use client"

import { ChevronDown, ListChecks, Settings2, Signal } from "lucide-react"
import { useMemo } from "react"

import { accountText, type LocaleProps } from "../_account-web/personal-web"
import { trackInstall } from "../_account-web/installation/analytics"
import { BrandPills } from "../_account-web/installation/components/BrandPills"
import { DeviceTabs } from "../_account-web/installation/components/DeviceTabs"
import { Hero } from "../_account-web/installation/components/Hero"
import { ImportantTips } from "../_account-web/installation/components/ImportantTips"
import { InstallMethodToggle } from "../_account-web/installation/components/InstallMethodToggle"
import { ManualCardPlaceholder } from "../_account-web/installation/components/ManualCardPlaceholder"
import { NetworkInfoBox } from "../_account-web/installation/components/NetworkInfoBox"
import { QRCardPlaceholder } from "../_account-web/installation/components/QRCardPlaceholder"
import { SectionHeader } from "../_account-web/installation/components/SectionHeader"
import { StepList } from "../_account-web/installation/components/StepList"
import { getFlow } from "../_account-web/installation/data/steps"
import { useDeviceDetect } from "../_account-web/installation/hooks/useDeviceDetect"
import { useInstallState } from "../_account-web/installation/hooks/useInstallState"
import {
  deviceKeyFor,
  splitDeviceKey,
  type AndroidBrand,
  type DeviceOs,
} from "../_account-web/installation/types"

/**
 * Public version of the install guide, rendered on /installation-guide.
 *
 * Mirrors the logged-in account/esim/{id}/installation layout 1:1 except:
 *   - No real eSIM data: QRCard → QRCardPlaceholder, ManualCard → ManualCardPlaceholder
 *   - No Tap-to-install button (it needs a real LPA)
 *   - No "eSIM expired" banner
 *   - Bottom adds a GuideCTABanner with shop / view-my-eSIM CTAs
 *
 * Same DeviceTabs / BrandPills / ImportantTips / SectionHeader / StepList /
 * InstallMethodToggle / NetworkInfoBox / Hero components, so any future
 * design change to the install flow shows up on both pages automatically.
 *
 * URL params `?device=` and `?method=` keep working — useInstallState
 * handles them, identical to the logged-in version, so customer support
 * can share `https://yohomobile.com/<lng>/installation-guide?device=android-samsung&method=manual`
 * links and they land in the right spot.
 */
export function PublicInstallationGuide({ lng }: LocaleProps) {
  const detected = useDeviceDetect()

  const defaultDevice = useMemo(() => {
    if (detected.os === "android") {
      return deviceKeyFor("android", detected.brand ?? "generic")
    }
    return deviceKeyFor("ios")
  }, [detected.os, detected.brand])

  const { device, method, setDevice, setMethod } = useInstallState({
    device: defaultDevice,
    method: "qr",
  })

  const { os, brand } = splitDeviceKey(device)
  const flow = getFlow(device)

  const handleOsChange = (next: DeviceOs) => {
    const nextDevice =
      next === "ios"
        ? deviceKeyFor("ios")
        : deviceKeyFor("android", brand ?? "generic")
    trackInstall({ name: "install_tab_changed", device: nextDevice, from: device })
    setDevice(nextDevice)
  }

  const handleBrandChange = (nextBrand: AndroidBrand) => {
    const nextDevice = deviceKeyFor("android", nextBrand)
    trackInstall({ name: "install_tab_changed", device: nextDevice, from: device })
    setDevice(nextDevice)
  }

  const handleMethodChange = (next: "qr" | "manual") => {
    trackInstall({
      name: "install_method_changed",
      device,
      method: next,
      from: method,
    })
    setMethod(next)
  }

  const installSteps =
    method === "manual" ? flow.install.manual : flow.install.qr

  return (
    <div className="grid gap-5 pb-6">
      <Hero lng={lng} />

      <p className="-mt-2 flex items-center justify-center gap-2 px-1 text-center text-[13px] font-medium text-stone-500">
        <span>{accountText(lng, "install.hero.orManual")}</span>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </p>

      <DeviceTabs lng={lng} device={device} onChange={handleOsChange} />

      {os === "android" ? (
        <BrandPills
          lng={lng}
          brand={
            brand === "samsung" || brand === "pixel" ? brand : "generic"
          }
          onChange={handleBrandChange}
        />
      ) : null}

      {/* ── Step 1: Before you install ───────────────────────────── */}
      <SectionHeader
        lng={lng}
        id="section-before"
        step={1}
        titleKey="install.section.beforeTitle"
        subtitleKey="install.section.beforeSubtitle"
        icon={ListChecks}
      />

      <ImportantTips lng={lng} />

      {/* ── Step 2: Install ───────────────────────────────────────── */}
      <SectionHeader
        lng={lng}
        id="section-install"
        step={2}
        titleKey="install.section.installTitle"
        subtitleKey="install.section.installSubtitle"
        icon={Settings2}
      />

      <InstallMethodToggle
        lng={lng}
        method={method === "deeplink" ? "qr" : method}
        onChange={handleMethodChange}
      />

      {method === "manual" ? (
        <ManualCardPlaceholder lng={lng} />
      ) : (
        <QRCardPlaceholder lng={lng} />
      )}

      <StepList
        lng={lng}
        steps={installSteps}
        ariaLabel={accountText(lng, "install.section.installAriaLabel")}
      />

      {/* ── Step 3: Connect ───────────────────────────────────────── */}
      <SectionHeader
        lng={lng}
        id="section-connect"
        step={3}
        titleKey="install.section.connectTitle"
        subtitleKey="install.section.connectSubtitle"
        icon={Signal}
      />

      <StepList
        lng={lng}
        steps={flow.connect}
        ariaLabel={accountText(lng, "install.section.connectAriaLabel")}
      />

      <NetworkInfoBox lng={lng} flow={flow} />
    </div>
  )
}
